import {
	EXTERNAL_ID,
	GENERIC_IDP_ID,
	GuideType,
	IdPCommonInputFieldName,
	MdComponentNames,
	Method,
	SCIMMethod,
	SUB
} from 'consts';
import { useAuthContext } from 'contexts/AuthContext';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import { cloneDeep } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { IAppConfig, IResIdPGuide, S4Feature } from 'types/api';
import {
	isFeatureEnabledForConfig,
	isGroupMappingDisabled,
	isS4FeatureEnabled
} from 'utils/s4FeaturesUtils';
import { TMethodGuideFieldsInfo } from 'types/fields';
import { extractGuideFieldsInfo } from 'utils/guideUtils';
import { filterObjectByValues } from 'utils/objectUtils';
import { get } from './api';
import { API_ROUTES } from './apiRoutes';
import { useAppConfig } from './appConfig';
import { useIsAuthenticationOnly } from './configurations';
import QueryKeys from './queryKeys';

const getIdPGuide = async (idpID: string) => {
	const res = await get<IResIdPGuide>(API_ROUTES.ssoTemplate(idpID));

	return res.data;
};

const stepComponents = (step: { content: string }): MdComponentNames[] =>
	Object.values(MdComponentNames).filter((componentMdName) =>
		step.content.includes(`<${componentMdName}`)
	);

// Whether a step holds the group attribute and nothing else, so hiding that component would leave
// the step blank. The stock mapping step pairs the user attributes with the group attributes and
// must survive, which is why this looks at every component rather than just the one.
const stepHoldsOnlyGroupsAttribute = (step: { content: string }): boolean => {
	const components = stepComponents(step);

	return (
		components.length > 0 &&
		components.every(
			(component) => component === MdComponentNames.GroupsAttribute
		)
	);
};

const finalizeGuide = (
	data: IResIdPGuide,
	appConfig: IAppConfig,
	authenticationOnly: boolean
): IResIdPGuide => {
	const clonedData = cloneDeep(data);

	for (const method of [Method.Oidc, Method.Saml]) {
		if (!clonedData.guides.sso?.[method]?.guide) {
			continue;
		}

		// Skipped before the step is built rather than filtered afterwards, because the page is
		// assembled here and a step that was never pushed cannot be left blank, counted, or landed
		// on by Continue.
		if (
			isFeatureEnabledForConfig(
				isS4FeatureEnabled(appConfig.features, S4Feature.SsoDomains),
				authenticationOnly,
				S4Feature.SsoDomains
			)
		) {
			const lines = [];
			if (appConfig.forceDomainVerification) {
				lines.push(
					intl.tr('ssoConfig.guide.domainsVerification.pageSubheader'),
					`<VerifyDomainInput />`
				);
			}
			lines.push(
				intl.tr('ssoConfig.guide.ssoDomains.pageSubheader'),
				`<IdPInputField fieldName="${IdPCommonInputFieldName.SSODomains}" displayName="${intl.tr('ssoConfig.fields.ssoDomains.fieldDisplayName')}" />`
			);

			clonedData.guides.sso[method].guide.push({
				title: intl.tr('ssoConfig.guide.ssoDomains.pageTitle'),
				content: lines.join('\n\n')
			});
		}

		if (isGroupMappingDisabled(appConfig.features)) {
			clonedData.guides.sso[method].guide = clonedData.guides.sso[
				method
			].guide.filter((step) => !stepHoldsOnlyGroupsAttribute(step));
		}

		// auto add sub mapping to the oidc guides
		if (method === Method.Oidc && clonedData.guides.sso[method]) {
			clonedData.guides.sso[method].mapping = {
				...clonedData.guides.sso[method].mapping,
				userAttributesDefaults: {
					[SUB]: EXTERNAL_ID,
					...clonedData.guides.sso[method].mapping?.userAttributesDefaults
				}
			};
		}
	}

	// XAA (Cross-App Access / ID-JAG) is content-driven exactly like OIDC/SAML: its guide
	// markdown ships in the content catalog under each provider template's guides.xaa
	// key, so there is NO client-side synthesis here - the content author controls which
	// fields appear, on which step, and in what order.

	return clonedData;
};

export const useIdpGuide = (idpID: string | null | undefined) => {
	const { authRes } = useAuthContext();
	const { data: appConfigData } = useAppConfig();
	const { data: authenticationOnly } = useIsAuthenticationOnly();
	// Whether the connection is known, which is what matters here - not whether the last fetch
	// succeeded. react-query keeps the data when a refetch fails and only moves the status to
	// error, and the configuration is invalidated by ordinary actions such as creating or rotating
	// a SCIM key, so reading isSuccess would empty the guide mid-session on one failed refetch.
	// select returns a boolean whenever there is data, so undefined means only "not loaded yet".
	const configsLoaded = authenticationOnly !== undefined;

	const selectFinalizeGuide = useCallback(
		(data: IResIdPGuide): IResIdPGuide =>
			finalizeGuide(data, appConfigData!, !!authenticationOnly),
		[appConfigData, authenticationOnly]
	);

	const query = useQuery({
		queryKey: [QueryKeys.idpGuides, idpID],
		queryFn: () => getIdPGuide(idpID!),
		enabled: authRes.verified && Boolean(idpID) && Boolean(appConfigData),
		select: selectFinalizeGuide,
		staleTime: Infinity
	});

	// Withheld until the configuration is in, rather than gating the query on it: a reader that saw
	// the unfiltered guide first could accept a deep link to a step that the filtered guide does not
	// have, and the step page dereferences that missing step. Gating `enabled` instead would make
	// this request wait for the configuration rather than run beside it.
	return useMemo(
		() => (configsLoaded ? query : { ...query, data: undefined }),
		[query, configsLoaded]
	);
};

export const useMethodGuide = (
	idpID: string | null | undefined,
	method: Method | null | undefined
) => {
	const { data, ...queryInfo } = useIdpGuide(method && idpID ? idpID : null);

	const queryDerivedData = useMemo(() => {
		if (!data) {
			return {
				data,
				...queryInfo
			};
		}

		return {
			...queryInfo,
			data: method ? data.guides.sso?.[method] : null
		};
	}, [data, method, queryInfo]);

	return queryDerivedData;
};

// Which SCIM guide a template pair yields: the selected method's, the template's own generic one,
// then the generic idp's. Shared by the two hooks below so their resolution cannot drift.
const resolveScimGuide = (
	data: IResIdPGuide | undefined,
	defaultIdp: IResIdPGuide | undefined,
	method: SCIMMethod | null | undefined
) =>
	(method ? data?.guides.scim?.[method] : null) ||
	data?.guides.scim?.[SCIMMethod.Generic] ||
	defaultIdp?.guides.scim?.[SCIMMethod.Generic] ||
	null;

export const useScimMethodGuide = (
	idpID: string | null | undefined,
	method: SCIMMethod | null | undefined
) => {
	const { data, ...queryInfo } = useIdpGuide(method && idpID ? idpID : null);
	const { data: defaultIdp } = useIdpGuide(GENERIC_IDP_ID);

	const queryDerivedData = useMemo(() => {
		if (!data) {
			return {
				data,
				...queryInfo
			};
		}

		return {
			...queryInfo,
			data: resolveScimGuide(data, defaultIdp, method)
		};
	}, [data, defaultIdp, method, queryInfo]);

	return queryDerivedData;
};

// XAA (Cross-App Access / ID-JAG) guide for the CURRENT-step fields-info only. Its guide
// markdown ships in the content catalog under the selected provider template's guides.xaa
// key (content-authored, no client-side synthesis), falling back to the generic idp -
// exactly like SAML/OIDC resolve the selected idp's template. Gated to the XAA flow so
// SSO/SCIM screens (which also compute current-guide fields-info) never fetch it.
export const useXaaGuide = () => {
	const { guideType, xaaIdpID } = useIdPConfigContext();
	const { data, ...queryInfo } = useIdpGuide(
		guideType === GuideType.XAA ? xaaIdpID || GENERIC_IDP_ID : null
	);

	return useMemo(
		() => ({
			...queryInfo,
			data: data?.guides.xaa?.[Method.Xaa] ?? null
		}),
		[data, queryInfo]
	);
};

// XAA guide for the sidebar/progress/step - fetched from the selected XAA provider's
// content template (the generic idp when none is selected, mirroring how SSO/SCIM fetch
// their guides), so the XAA sidebar section lists its content-authored steps and shows
// progress consistently, not only once inside the XAA flow.
export const useXaaMethodGuide = () => {
	const { xaaIdpID } = useIdPConfigContext();
	const { data, ...queryInfo } = useIdpGuide(xaaIdpID || GENERIC_IDP_ID);

	return useMemo(
		() => ({
			...queryInfo,
			data: data?.guides.xaa?.[Method.Xaa] ?? null
		}),
		[data, queryInfo]
	);
};

// SCIM guide for the current-guide/step fields-info only. Same resolution as useScimMethodGuide
// (shared via resolveScimGuide) but gated to the SCIM flow, so SSO/XAA screens - which compute
// fields-info through the same hooks - never fetch the SCIM template. Inside a SCIM guide these
// are the queries useCurrentStep and the sidebar already issue, served from the same cache keys.
const useScimGuide = () => {
	const { guideType, scimIdpID, scimMethod } = useIdPConfigContext();
	const inScimGuide = guideType === GuideType.SCIM && Boolean(scimIdpID);

	const { data } = useIdpGuide(inScimGuide ? scimIdpID : null);
	const { data: defaultIdp } = useIdpGuide(inScimGuide ? GENERIC_IDP_ID : null);

	return useMemo(
		() => resolveScimGuide(data, defaultIdp, scimMethod),
		[data, defaultIdp, scimMethod]
	);
};

export const useCurrentMethodGuideFieldsInfo = (): TMethodGuideFieldsInfo => {
	const { method, idpID, guideType } = useIdPConfigContext();

	const { data: ssoGuideData } = useMethodGuide(idpID, method);
	const { data: xaaGuideData } = useXaaGuide();
	const scimGuideData = useScimGuide();

	// Every guide type declares its own fields: a SCIM guide's mapping steps live in the SCIM
	// template, not the SSO one it borrows the mapping from, so reading the SSO guide here would
	// mask a SCIM step's errors against fields that step never renders.
	const idpGuideData = {
		[GuideType.SSO]: ssoGuideData,
		[GuideType.XAA]: xaaGuideData,
		[GuideType.SCIM]: scimGuideData
	}[guideType ?? GuideType.SSO];

	return useMemo(() => {
		if (!idpGuideData) {
			return {};
		}
		return extractGuideFieldsInfo(idpGuideData);
	}, [idpGuideData]);
};

export const useCurrentStepFieldsInfo = (): TMethodGuideFieldsInfo => {
	const { guideStepNum, xaaGuideStepNum, scimGuideStepNum, guideType } =
		useIdPConfigContext();
	const stepNum = {
		[GuideType.SSO]: guideStepNum,
		[GuideType.XAA]: xaaGuideStepNum,
		[GuideType.SCIM]: scimGuideStepNum
	}[guideType ?? GuideType.SSO];
	const guideFieldsInfo = useCurrentMethodGuideFieldsInfo();
	const stepFieldsInfo = useMemo(
		() =>
			filterObjectByValues(
				guideFieldsInfo,
				(value) => value?.stepNum === stepNum
			),
		[guideFieldsInfo, stepNum]
	);
	return stepFieldsInfo;
};

export const useCurrentStep = () => {
	const {
		guideStepNum,
		method,
		idpID,
		guideType,
		scimIdpID,
		scimMethod,
		scimGuideStepNum,
		xaaGuideStepNum
	} = useIdPConfigContext();
	const { data: idpGuideData, ...queryInfo } = useMethodGuide(idpID, method);
	const { data: scimIdPGuideData, ...scimQueryInfo } = useScimMethodGuide(
		scimIdpID,
		scimMethod
	);
	// Gated to the XAA flow (useXaaGuide) so SSO/SCIM guide pages don't fetch the
	// XAA provider's template just to render their own step.
	const { data: xaaGuideData, ...xaaQueryInfo } = useXaaGuide();

	const currentNum =
		// eslint-disable-next-line no-nested-ternary
		guideType === GuideType.SSO
			? guideStepNum
			: guideType === GuideType.XAA
				? xaaGuideStepNum
				: scimGuideStepNum;
	const currentMethod =
		// eslint-disable-next-line no-nested-ternary
		guideType === GuideType.SSO
			? method
			: guideType === GuideType.XAA
				? Method.Xaa
				: scimMethod;

	const queryDerivedData = useMemo(() => {
		// eslint-disable-next-line no-nested-ternary
		const guideData =
			// eslint-disable-next-line no-nested-ternary
			guideType === GuideType.SSO
				? idpGuideData
				: guideType === GuideType.XAA
					? xaaGuideData
					: scimIdPGuideData;
		const guide = guideData?.guide;
		if (!currentNum || !guideType || !currentMethod || !guide) {
			/* istanbul ignore next */
			return {
				...queryInfo,
				data: null
			};
		}
		const step = guide[currentNum - 1];
		const isLastStep = currentNum === guide.length;

		// eslint-disable-next-line no-nested-ternary
		const activeQueryInfo =
			// eslint-disable-next-line no-nested-ternary
			guideType === GuideType.SSO
				? queryInfo
				: guideType === GuideType.XAA
					? xaaQueryInfo
					: scimQueryInfo;

		return {
			...activeQueryInfo,
			data: {
				step,
				stepNum: currentNum,
				total: guide.length,
				isLastStep
			}
		};
	}, [
		currentMethod,
		idpGuideData,
		scimIdPGuideData,
		xaaGuideData,
		currentNum,
		guideType,
		queryInfo,
		scimQueryInfo,
		xaaQueryInfo
	]);

	return queryDerivedData;
};
